import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { AuthContext } from "../../contexts/auth";
import ProjectService from "../../services/project.service";

export type PreEventD1Alert = {
  projectId: string;
  eventName: string;
  mmCode?: string | number | null;
  yearCode?: string | number | null;
  startDate: string;
  budgetApproved: boolean;
};

type NotificationKind = "budget" | "confirm";

export type StackNotification = {
  id: string;
  kind: NotificationKind;
  title: string;
  description: string;
  actionLabel: string;
  path: string;
};

export const VISIBLE_LIMIT = 3;
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function projectCode(alert: PreEventD1Alert) {
  const hasCode = alert.mmCode != null && alert.mmCode !== "";
  const hasYear = alert.yearCode != null && alert.yearCode !== "";
  if (!hasCode) return "";
  return hasYear
    ? `MM ${alert.mmCode}.${alert.yearCode}`
    : `MM ${alert.mmCode}`;
}

function withSubject(prefix: string, alert: PreEventD1Alert) {
  const subject = projectCode(alert) || alert.eventName?.trim() || "";
  return subject ? `${prefix} — ${subject}` : prefix;
}

function withEventName(alert: PreEventD1Alert, detail: string) {
  const name = projectCode(alert) ? alert.eventName?.trim() : "";
  return name ? `${name} · ${detail}` : detail;
}

export function toNotifications(
  alerts: PreEventD1Alert[]
): StackNotification[] {
  return alerts.flatMap((alert) => {
    const items: StackNotification[] = [];
    if (!alert.budgetApproved) {
      items.push({
        id: `pre-event-budget-${alert.projectId}`,
        kind: "budget",
        title: withSubject("Atenção", alert),
        description: withEventName(alert, "orçamento ainda não aprovado"),
        actionLabel: "Ir para Orçamentos",
        path: `/projeto?projectId=${alert.projectId}&tab=budget`,
      });
    }
    items.push({
      id: `pre-event-confirm-${alert.projectId}`,
      kind: "confirm",
      title: withSubject("Confirmação pré-evento", alert),
      description: withEventName(
        alert,
        `começa amanhã (${alert.startDate}). Confirme o Tipo do Evento e a Quantidade de Pessoas`
      ),
      actionLabel: "Revisar dados",
      path: `/projeto?projectId=${alert.projectId}&subTab=event`,
    });
    return items;
  });
}

function countLabel(count: number) {
  return count === 1 ? "1 notificação" : `${count} notificações`;
}

function hasOpenDialog() {
  return (
    document.querySelector(
      '[role="dialog"], [role="presentation"].MuiModal-root'
    ) !== null
  );
}

type NotificationCardProps = {
  notification: StackNotification;
  onClose: () => void;
  onAction: () => void;
};

function NotificationCard({
  notification,
  onClose,
  onAction,
}: NotificationCardProps) {
  const isBudget = notification.kind === "budget";
  const Icon = isBudget ? ErrorOutlineIcon : InfoOutlinedIcon;

  return (
    <Paper
      variant="outlined"
      role="article"
      aria-label={notification.title}
      sx={{ p: 1.5, borderRadius: 2, display: "flex", gap: 1.25 }}
    >
      <Icon
        fontSize="small"
        sx={{ mt: "2px", color: isBudget ? "#b7791f" : "#1976d2" }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>
          {notification.title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
          {notification.description}
        </Typography>
        <Button
          size="small"
          onClick={onAction}
          sx={{ mt: 0.5, ml: -0.75, textTransform: "none", fontSize: 13 }}
        >
          {notification.actionLabel}
        </Button>
      </Box>
      <IconButton
        size="small"
        aria-label={`Fechar notificação ${notification.title}`}
        onClick={onClose}
        sx={{ alignSelf: "flex-start", mt: -0.5, mr: -0.5 }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Paper>
  );
}

type NotificationStackProps = {
  notifications: StackNotification[];
  onNavigate: (path: string) => void;
};

export function NotificationStack({
  notifications,
  onNavigate,
}: NotificationStackProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const active = useMemo(
    () => notifications.filter((item) => !dismissed.has(item.id)),
    [notifications, dismissed]
  );
  const isStackVisible = active.length > 0 && !minimized;
  // Depois de fechar notificações o sino fica disponível no canto da tela.
  const showBell = !isStackVisible && (minimized || dismissed.size > 0);

  const dismiss = useCallback((ids: string[]) => {
    setDismissed((prev) => new Set([...Array.from(prev), ...ids]));
  }, []);

  const closeAll = () => {
    dismiss(active.map((item) => item.id));
    setMinimized(false);
    setExpanded(false);
  };

  useEffect(() => {
    if (!isStackVisible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || hasOpenDialog()) {
        return;
      }
      setMinimized(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isStackVisible]);

  if (isStackVisible) {
    const visible = expanded ? active : active.slice(0, VISIBLE_LIMIT);
    const hiddenCount = active.length - visible.length;

    return (
      <Box
        role="region"
        aria-label="Notificações"
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 1250,
          width: 360,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            boxShadow: 2,
          }}
        >
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>
            {countLabel(active.length)}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RemoveIcon fontSize="small" />}
            onClick={() => setMinimized(true)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Minimizar
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<CloseIcon fontSize="small" />}
            onClick={closeAll}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Fechar todas
          </Button>
        </Paper>

        <Box
          sx={{
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            "& > .MuiPaper-root": { boxShadow: 2, flexShrink: 0 },
          }}
        >
          {visible.map((item) => (
            <NotificationCard
              key={item.id}
              notification={item}
              onClose={() => dismiss([item.id])}
              onAction={() => onNavigate(item.path)}
            />
          ))}
        </Box>

        {hiddenCount > 0 && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setExpanded(true)}
            sx={{
              alignSelf: "center",
              textTransform: "none",
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": { bgcolor: "#f5f9ff" },
            }}
          >
            + {countLabel(hiddenCount)}
          </Button>
        )}
        {expanded && active.length > VISIBLE_LIMIT && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setExpanded(false)}
            sx={{
              alignSelf: "center",
              textTransform: "none",
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": { bgcolor: "#f5f9ff" },
            }}
          >
            Mostrar menos
          </Button>
        )}
      </Box>
    );
  }

  if (!showBell) return null;

  const pending = minimized ? active.length : 0;

  return (
    <IconButton
      aria-label={
        pending > 0
          ? `Abrir ${countLabel(pending)}`
          : "Nenhuma notificação pendente"
      }
      onClick={() => {
        if (pending > 0) setMinimized(false);
      }}
      sx={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 1250,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 2,
        "&:hover": { bgcolor: "background.paper" },
      }}
    >
      <Badge badgeContent={pending} color="error" invisible={pending === 0}>
        <NotificationsNoneIcon />
      </Badge>
    </IconButton>
  );
}

export function PreEventD1Notifications() {
  const { user, isLoading } = useContext(AuthContext);
  const history = useHistory();
  const [notifications, setNotifications] = useState<StackNotification[]>([]);
  const userId = user?.id;

  useEffect(() => {
    setNotifications([]);
    if (isLoading || !userId) {
      return;
    }

    let cancelled = false;
    const load = () => {
      ProjectService.getPreEventD1Alerts()
        .then((alerts) => {
          if (cancelled || !Array.isArray(alerts)) return;
          setNotifications(toNotifications(alerts));
        })
        .catch(() => undefined);
    };

    load();
    const interval = window.setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isLoading, userId]);

  if (!userId) return null;

  // `key` zera o que foi fechado/minimizado a cada login.
  return (
    <NotificationStack
      key={userId}
      notifications={notifications}
      onNavigate={(path) => history.push(path)}
    />
  );
}
